"use client";

import {
  Box,
  Stack,
  styled,
  useTheme,
  Typography,
  Button as MuiButton,
  CircularProgress,
} from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import useWindowSize from "../../../hooks/useWindowSize";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { Upload, Check, AlertCircle } from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import Iconify from "../../iconify/iconify";

const StyledMinImgBox = styled(Box)(({ theme }) => ({
  height: "500px",
  borderRadius: "10px",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "320px",
  },
}));

const StyledIndexBox = styled(Box)(({ theme }) => ({
  width: "15px",
  height: "5px",
  borderRadius: "50px",
  cursor: "pointer",
  background: theme.palette.background.light,
  "&.active": {
    background: "#a95052",
  },
}));

const StyledArrowBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  padding: "10px",
  border: `1px solid ${theme.palette.text.secondary}`,
  color: theme.palette.text.secondary,
  cursor: "pointer",
}));

const DropZone = styled(Box)(({ theme, isDragging }) => ({
  height: "500px",
  width: "100%",
  borderRadius: "10px",
  border: `2px dashed ${
    isDragging ? theme.palette.primary.main : theme.palette.text.secondary
  }`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isDragging ? "rgba(0, 0, 0, 0.05)" : "transparent",
  transition: "all 0.3s ease",
  cursor: "pointer",
  [theme.breakpoints.down("sm")]: {
    height: "320px",
  },
}));

const DeleteButton = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "10px",
  right: "10px",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  borderRadius: "50%",
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 10,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(169, 80, 82, 0.8)",
    transform: "scale(1.1)",
  },
}));

const UploadButton = styled(MuiButton)(({ theme }) => ({
  position: "absolute",
  bottom: "10px",
  right: "10px",
  zIndex: 10,
  backgroundColor: "#a95052",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#8a3f41",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: "6px 12px",
  },
}));

const StatusMessage = styled(Box)(({ theme, status }) => ({
  position: "absolute",
  bottom: "60px",
  right: "10px",
  zIndex: 10,
  padding: "8px 12px",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor:
    status === "success"
      ? "rgba(46, 125, 50, 0.9)"
      : status === "error"
      ? "rgba(211, 47, 47, 0.9)"
      : "rgba(0, 0, 0, 0.7)",
  color: "#fff",
  fontSize: "0.875rem",
  maxWidth: "calc(100% - 20px)",
  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
}));

const UploadHauseCarousel = ({
  initialImages = [],
  onImagesChange,
  onUpload,
}) => {
  const theme = useTheme();
  const screenWidth = useWindowSize();
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imgData, setImgData] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [statusMessage, setStatusMessage] = useState("");

  // Initialize with server images if provided
  useEffect(() => {
    if (initialImages && initialImages.length > 0) {
      const serverImageItems = initialImages.map((url) => ({
        url,
        isFromServer: true,
      }));
      setImgData(serverImageItems);
    }
  }, [initialImages]);

  // Notify parent component when images change
  useEffect(() => {
    if (onImagesChange) {
      // Extract just the URLs for the callback
      const urls = imgData.map((item) => item.url);
      onImagesChange(urls);
    }
  }, [imgData, onImagesChange]);

  const handleIndexClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
      setActiveIndex(index);
    }
  };

  const handleSlideChange = () => {
    if (swiper) {
      setActiveIndex(swiper.realIndex);
    }
  };

  const handleSwiperInit = (swiper) => {
    setSwiper(swiper);
  };

  const handlePrevButton = () => {
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const handleNextButton = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newImages = [];

      Array.from(files).forEach((file) => {
        if (file.type.match("image.*")) {
          const imageUrl = URL.createObjectURL(file);
          newImages.push({
            url: imageUrl,
            file: file,
            isFromServer: false,
          });
        }
      });

      if (newImages.length > 0) {
        setImgData((prevImages) => [...prevImages, ...newImages]);
      }
    }
  }, []);

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newImages = [];

      Array.from(files).forEach((file) => {
        if (file.type.match("image.*")) {
          const imageUrl = URL.createObjectURL(file);
          newImages.push({
            url: imageUrl,
            file: file,
            isFromServer: false,
          });
        }
      });

      if (newImages.length > 0) {
        setImgData((prevImages) => [...prevImages, ...newImages]);
      }
    }
  };

  const handleDeleteImage = (indexToDelete) => {
    setImgData((prevImages) => {
      const newImages = [...prevImages];
      newImages.splice(indexToDelete, 1);

      // Reset active index if needed
      if (newImages.length === 0) {
        setActiveIndex(0);
      } else if (activeIndex >= newImages.length) {
        setActiveIndex(newImages.length - 1);
      }

      return newImages;
    });
  };

  const handleUploadImages = async () => {
    if (imgData.length === 0) {
      setUploadStatus("error");
      setStatusMessage("No hay imágenes para subir");
      setTimeout(() => {
        setUploadStatus(null);
        setStatusMessage("");
      }, 3000);
      return;
    }

    setUploadStatus("loading");
    setStatusMessage("Subiendo imágenes...");

    try {
      // Prepare all images in carousel order with their index
      const uploadItems = imgData.map((img, index) => ({
        ...img,
        index,
      }));

      // Count new images for status message
      const newImagesCount = uploadItems.filter(
        (item) => !item.isFromServer
      ).length;

      // Use custom upload handler if provided
      if (onUpload) {
        await onUpload(uploadItems);
        setUploadStatus("success");
        setStatusMessage(
          newImagesCount > 0
            ? `${newImagesCount} imágenes nuevas subidas correctamente`
            : "Imágenes procesadas correctamente"
        );

        // Mark all images as from server after successful upload
        setImgData((prevImages) =>
          prevImages.map((img) => ({ ...img, isFromServer: true }))
        );
      } else {
        // Mock upload process if no custom handler
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Simulate success (80% chance) or error (20% chance)
        const isSuccess = Math.random() > 0.2;

        if (isSuccess) {
          setUploadStatus("success");
          setStatusMessage(
            newImagesCount > 0
              ? `${newImagesCount} imágenes nuevas subidas correctamente`
              : "Imágenes procesadas correctamente"
          );

          // Mark all images as from server
          setImgData((prevImages) =>
            prevImages.map((img) => ({ ...img, isFromServer: true }))
          );
        } else {
          throw new Error("Error al subir las imágenes");
        }
      }
    } catch (error) {
      setUploadStatus("error");
      setStatusMessage(error.message || "Error al subir las imágenes");
    }

    // Clear status after 3 seconds
    setTimeout(() => {
      setUploadStatus(null);
      setStatusMessage("");
    }, 3000);
  };

  return (
    <Stack
      p={{ xs: "15px", md: "40px" }}
      mb="30px"
      bgcolor={theme.palette.background.light}
      borderRadius="10px"
      position="relative"
    >
      {imgData.length > 0 && (
        <>
          <UploadButton
            variant="contained"
            startIcon={
              uploadStatus === "loading" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Upload size={16} />
              )
            }
            onClick={handleUploadImages}
            disabled={uploadStatus === "loading"}
          >
            Subir imágene
          </UploadButton>

          {uploadStatus && (
            <StatusMessage status={uploadStatus}>
              {uploadStatus === "success" && <Check size={16} />}
              {uploadStatus === "error" && <AlertCircle size={16} />}
              {statusMessage}
            </StatusMessage>
          )}
        </>
      )}

      <Stack direction="row">
        {imgData.length === 0 ? (
          <Box
            component="label"
            htmlFor="file-upload"
            sx={{ width: "100%", cursor: "pointer" }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              id="file-upload"
              type="file"
              multiple
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />
            <DropZone isDragging={isDragging}>
              <Upload size={48} color={theme.palette.text.secondary} />
              <Typography variant="h6" color="text.secondary" mt={2}>
                Arrastre y suelte imágenes aquí
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                o haga clic para seleccionar archivos
              </Typography>
            </DropZone>
          </Box>
        ) : (
          <Box
            sx={{ width: "100%" }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Swiper
              grabCursor={true}
              centeredSlides={false}
              slidesPerView={screenWidth < 1000 ? 1 : 2}
              spaceBetween={10}
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 2,
                slideShadows: false,
              }}
              loop={imgData.length > 1}
              className="mySwiper"
              onSlideChange={handleSlideChange}
              onSwiper={handleSwiperInit}
              modules={[Navigation, Pagination]}
            >
              {imgData.map((image, index) => (
                <SwiperSlide key={index}>
                  <StyledMinImgBox sx={{ position: "relative" }}>
                    <DeleteButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(index);
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: "24px", lineHeight: 1 }}
                      >
                        ×
                      </Box>
                    </DeleteButton>
                    <Box
                      component="img"
                      width="100%"
                      height="100%"
                      src={image.url}
                      alt={`slide-${index}`}
                      style={{ objectFit: "cover" }}
                    />
                  </StyledMinImgBox>
                </SwiperSlide>
              ))}
              <SwiperSlide>
                <Box
                  component="label"
                  htmlFor="file-upload-carousel"
                  sx={{ width: "100%", cursor: "pointer" }}
                >
                  <input
                    id="file-upload-carousel"
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                  />
                  <DropZone isDragging={isDragging}>
                    <Upload size={48} color={theme.palette.text.secondary} />
                    <Typography variant="h6" color="text.secondary" mt={2}>
                      Añadir más imágenes
                    </Typography>
                  </DropZone>
                </Box>
              </SwiperSlide>
            </Swiper>
          </Box>
        )}
      </Stack>
      {imgData.length > 0 && (
        <Stack direction="row" justifyContent="center" mt={{ xs: 2, md: 5 }}>
          <Box
            padding="10px"
            borderRadius="50px"
            bgcolor={theme.palette.background.default}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <StyledArrowBox onClick={handlePrevButton}>
                <Iconify icon="grommet-icons:link-previous" />
              </StyledArrowBox>
              {imgData
                .slice(0, screenWidth <= 600 ? 10 : imgData.length)
                .map((_, index) => (
                  <StyledIndexBox
                    key={index}
                    onClick={() => handleIndexClick(index)}
                    className={activeIndex === index ? "active" : ""}
                  />
                ))}
              <StyledArrowBox onClick={handleNextButton}>
                <Iconify icon="ooui:arrow-previous-rtl" />
              </StyledArrowBox>
            </Stack>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default UploadHauseCarousel;
